import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '../ui/data-table';
import { classAPI } from '../../services/api';

const Instructor = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topic, setTopics] = useState([]);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  const columns = [
    {
      accessorKey: 'id',
      header: 'ID',
      sortable: true
    },
    {
      accessorKey: 'title',
      header: 'Title',
      sortable: true
    },
    {
      accessorKey: 'description',
      header: 'Description',
      sortable: false
    },
    {
      accessorKey: 'class_id',
      header: 'Class ID',
      sortable: true
    },
    {
      accessorKey: 'class_code',
      header: 'Class Code',
      sortable: true
    },
    {
      accessorKey: 'course_id',
      header: 'Course ID',
      sortable: true
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      sortable: true,
      cell: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A'
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      sortable: false,
      className: 'w-32',
      cell: (row) => (
        <div className="flex space-x-1">
          <button
            onClick={() => navigate(`/classes/${row.id}/shared-files`, { state: { classTitle: row.title } })}
            className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
            title="Хичээлийн материал"
          >
            Материал
          </button>
          <button
            onClick={() => handleViewAttendance(row, user)}
            className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
            title="View class attendance"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          </button>
        </div>
      )
    }
  ];

  useEffect(() => {
    // Get user data from storage
    const storedUser = localStorage.getItem('adminUser') || sessionStorage.getItem('adminUser');
    
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        
        // Use the instructor ID to fetch classes
        if (userData.id) {
          fetchInstructorClasses(userData.id);
        } else {
          setError('No instructor ID found in user data');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error parsing user data:', err);
        setError('Invalid user data');
        setLoading(false);
      }
    } else {
      setError('No user data found. Please log in again.');
      setLoading(false);
    }
  }, []);

  const fetchInstructorClasses = async (instructorId) => {  
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching classes for instructor ID:', instructorId);
      const response = await classAPI.getClassesByInstructorId(instructorId);
      
      console.log('Instructor classes response:', response);
      setClasses(response.data || []);
      setTopics(response.topics || []);
    } catch (error) {
      console.error('Error fetching instructor classes:', error);
      setError('Failed to load instructor classes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAttendance = (classItem,user) => {
    const instructorId = user ? user.id : null;
    navigate(`/classes/${classItem.id}/attendance?instructorId=${instructorId}`, {
      state: { 
        classId: classItem.id, 
        classTitle: classItem.title,
        instructorId: instructorId
      }
    });
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-gray-600">Loading your classes...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Data</h2>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 border-b border-gray-200 pb-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Classes</h1>
          <p className="text-lg text-gray-600">
            {user ? `Welcome, ${user.first_name || user.username}` : 'Instructor Dashboard'}
          </p>
          <p className="text-sm text-gray-500">
            Manage classes assigned to you ({classes.length} classes)
          </p>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={classes}
        searchKey="title"
        searchPlaceholder="Search your classes..."
        loading={loading}
        emptyMessage="No classes assigned to you yet."
      />
    </div>
  );
};

export default Instructor;
